import React, { useState, useEffect } from 'react';
import { Send, MessageSquare, Plus, X, ChevronDown, ChevronUp, Trash2, Users, User } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [form, setForm] = useState({ subject: '', body: '', recipients: 'all_players' });
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    fetchMessages();
    fetchPlayers();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/messages');
      setMessages(res.data);
    } catch { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  };

  const fetchPlayers = async () => {
    try {
      const res = await api.get('/players');
      setPlayers(res.data);
    } catch {}
  };

  const sendMessage = async () => {
    if (!form.subject || !form.body) { toast.error('Please fill in subject and message'); return; }
    try {
      const res = await api.post('/messages', form);
      setMessages([res.data, ...messages]);
      setForm({ subject: '', body: '', recipients: 'all_players' });
      setShowCompose(false);
      toast.success('Message sent to all players');
    } catch { toast.error('Failed to send message'); }
  };

  const sendReply = async (msgId) => {
    if (!replyText.trim()) return;
    try {
      const res = await api.post(`/messages/${msgId}/reply`, { body: replyText });
      setMessages(messages.map(m => m.id === msgId ? { ...m, replies: [...(m.replies || []), res.data] } : m));
      setReplyText('');
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
  };

  const deleteMessage = async (msgId) => {
    if (!window.confirm('Delete this message and all replies?')) return;
    try {
      await api.delete(`/messages/${msgId}`);
      setMessages(messages.filter(m => m.id !== msgId));
      toast.success('Message deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white" data-testid="messages-title">Messages</h1>
          <p className="text-sm text-zinc-500 mt-1">Broadcast to player portals</p>
        </div>
        <button onClick={() => setShowCompose(!showCompose)} data-testid="compose-message-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide text-sm rounded-sm transition-all">
          <Plus className="w-4 h-4" /> New Message
        </button>
      </div>

      {showCompose && (
        <div className="glass-card p-6 mb-8 border border-[#FFFFFF]/20" data-testid="compose-form">
          <h3 className="text-lg font-bold uppercase text-white mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-white" /> Compose Broadcast
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Send To</label>
              <select value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} data-testid="msg-recipients"
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm outline-none">
                <option value="all_players">All Players</option>
                {players.sort((a,b) => a.name.localeCompare(b.name)).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Subject</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="msg-subject"
                placeholder="Message subject..." className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Message</label>
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="msg-body"
                rows="4" placeholder="Type your message to players..."
                className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 outline-none resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={sendMessage} data-testid="send-message-btn"
                className="px-5 py-2.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-sm rounded-sm transition-all flex items-center gap-2">
                <Send className="w-4 h-4" /> Send
              </button>
              <button onClick={() => setShowCompose(false)}
                className="px-5 py-2.5 bg-white/10 text-white hover:bg-white/20 text-sm rounded-sm transition-all flex items-center gap-2">
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {messages.length > 0 ? messages.map((msg) => (
          <div key={msg.id} className="glass-card overflow-hidden" data-testid={`message-${msg.id}`}>
            <div className="p-5 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setExpandedMsg(expandedMsg === msg.id ? null : msg.id)}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-white flex-shrink-0" />
                    <h3 className="text-white font-bold truncate">{msg.subject}</h3>
                    {(msg.replies?.length > 0) && (
                      <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                        {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm line-clamp-1">{msg.body}</p>
                  <p className="text-zinc-600 text-xs mt-1">
                    Sent by {msg.sender_name} · {formatDate(msg.created_at)} · To: {msg.recipients === 'all_players' ? 'All Players' : players.find(p => p.id === msg.recipients)?.name || msg.recipients}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }} data-testid={`delete-msg-${msg.id}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  {expandedMsg === msg.id ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </div>
              </div>
            </div>

            {expandedMsg === msg.id && (
              <div className="border-t border-white/10">
                <div className="p-5 bg-white/[0.02]">
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap mb-4">{msg.body}</p>
                  {msg.replies?.length > 0 && (
                    <div className="space-y-3 mb-4">
                      <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Replies</p>
                      {msg.replies.map((r) => (
                        <div key={r.id} className={`p-3 rounded-lg ${r.sender_role === 'admin' ? 'bg-white/10 border border-[#FFFFFF]/20' : 'bg-white/5 border border-white/10'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-3 h-3 text-zinc-400" />
                            <span className="text-xs font-bold text-zinc-400">{r.sender_name}</span>
                            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.sender_role === 'admin' ? 'bg-white/20 text-white' : 'bg-blue-500/20 text-blue-400'}`}>
                              {r.sender_role}
                            </span>
                            <span className="text-zinc-600 text-xs">{formatDate(r.created_at)}</span>
                          </div>
                          <p className="text-zinc-300 text-sm whitespace-pre-wrap">{r.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input value={replyText} onChange={(e) => setReplyText(e.target.value)} data-testid={`reply-input-${msg.id}`}
                      placeholder="Type a reply..." onKeyDown={(e) => e.key === 'Enter' && sendReply(msg.id)}
                      className="flex-1 bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none" />
                    <button onClick={() => sendReply(msg.id)} data-testid={`reply-btn-${msg.id}`}
                      className="px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all flex items-center gap-1">
                      <Send className="w-3 h-3" /> Reply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )) : (
          <div className="glass-card p-12 text-center">
            <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No messages yet. Send your first broadcast to players!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
